import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import * as XLSX from 'xlsx';

import { isGameExpired, formatGameEndDate } from '@shared/utils/gameExpiration';

import './Dashboard.scss';

interface PartnerGame {
  _id: string;
  gameName: string;
  notion: string;
  email?: string;
  key: string;
  gameBuildId: string;
  gameBuild?: any; // Объект gameBuild
  createdAt: string;
  activateDate?: string;
  gameDayCount: number;
  editDayCount: number;
  runCount: number;
  isActive: boolean;
  isCreated?: boolean;
}

interface PartnerProfile {
  id: string;
  email: string;
  apiKey: string;
  defaultGameDayCount: number;
  defaultEditDayCount: number;
  defaultIsWhiteLabel: boolean;
  createdAt: string;
  keysAvailable: string;
}

const Dashboard: React.FC = () => {
  const [games, setGames] = useState<PartnerGame[]>([]);
  const [profile, setProfile] = useState<PartnerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [createForm, setCreateForm] = useState({ 
    gameTitle: '', 
    notion: '', 
    keyCount: 1,
    urlButton: ''
  });
  const [availableGames, setAvailableGames] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [monthlyStats, setMonthlyStats] = useState(0);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  const navigate = useNavigate();

  // Загрузка профиля и игр
  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (error === 'Ошибка загрузки данных' || error === 'Ошибка загрузки профиля') {
      navigate('/partners/login');
    }
  }, [error, navigate]);

  // Функция для проверки истекших игр
  const checkExpiredGames = (gamesList: PartnerGame[]) => {
    return gamesList.map(game => {
      if (game.activateDate && game.gameDayCount) {
        const activateDate = new Date(game.activateDate);
        
        // Если игра истекла, но еще активна, обновляем статус
        if (isGameExpired(activateDate, game.gameDayCount) && game.isActive) {
          return { ...game, isActive: false };
        }
      }
      return game;
    });
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Загружаем профиль
      const profileResponse = await fetch('/api/partner/profile', {
        credentials: 'include',
      });
      
      if (!profileResponse.ok) {
        throw new Error('Не авторизован');
      }
      
      const profileData = await profileResponse.json();
      setProfile(profileData.data.partner);

      // Загружаем игры
      const gamesResponse = await fetch('/api/partner/games', {
        credentials: 'include',
      });
      
      if (gamesResponse.ok) {
        const gamesData = await gamesResponse.json();
        const updatedGames = checkExpiredGames(gamesData.data.games);
        setGames(updatedGames);
        setMonthlyStats(calculateMonthlyStats(updatedGames));
      }

      // Загружаем доступные игры из коллекции games
      const availableGamesResponse = await fetch('/api/partner/available-games', {
        credentials: 'include',
      });
      
      if (availableGamesResponse.ok) {
        const availableGamesData = await availableGamesResponse.json();
        setAvailableGames(availableGamesData.data.games.map((game: any) => game.title));
      } else {
        // Fallback на захардкоженный список, если API недоступен
        setAvailableGames(['Memory', 'Kids 001', 'Kids 002', 'Puzzle']);
      }
      
    } catch (err) {
      setError('Ошибка загрузки данных');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/partner/logout', {
        method: 'POST',
        credentials: 'include',
      });
      navigate('/partners/login');
    } catch (err) {
      console.error('Ошибка выхода:', err);
    }
  };

  const handleCreateGame = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch('/api/partner/games', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(createForm),
      });

      if (response.ok) {
        setCreateForm({ gameTitle: '', notion: '', keyCount: 1, urlButton: '' });
        loadData(); // Перезагружаем список игр
        setError(''); // Очищаем ошибки
      } else {
        const data = await response.json();
        setError(data.message || 'Ошибка создания игры');
      }
    } catch (err) {
      setError('Ошибка соединения с сервером');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Можно добавить уведомление об успешном копировании
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const calculateEndDate = (startDate: string, days: number) => {
    const start = new Date(startDate);
    const end = new Date(start.getTime() + days * 24 * 60 * 60 * 1000);
    return end.toLocaleDateString('ru-RU');
  };

  const getGameUrl = (key: string) => {
    return `${window.location.origin}/login?key=${key}`;
  };

  const getPlayUrl = (buildId: string) => {
    return `${window.location.origin}/game?buildId=${buildId}`;
  };

  const handleGameClick = (buildId: string) => {
    window.open(getPlayUrl(buildId), '_blank');
  };

  const handleEditorClick = (key: string) => {
    window.open(getGameUrl(key), '_blank');
  };

  const calculateMonthlyStats = (games: PartnerGame[]) => {
    const now = new Date();
    const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    
    return games.filter(game => {
      const gameDate = new Date(game.createdAt);
      return gameDate >= monthAgo;
    }).length;
  };

  const handleExportExcel = () => {
    const exportData = games.map(game => ({
      'Название игры': game.gameName,
      'Email игрока': game.email || '-',
      'Заметка': game.notion || '-',
      'Дата создания': formatDate(game.createdAt),
      'Ключ игры': game.key,
      'Запусков': game.runCount,
      'Дата активации': game.activateDate ? formatDate(game.activateDate) : '-',
      'Редактирование до': (game.gameBuild && game.gameBuild.createdAt) ? calculateEndDate(game.gameBuild.createdAt, game.editDayCount) : '-',
      'Дата отключения': (game.gameBuild && game.gameBuild.createdAt) ? calculateEndDate(game.gameBuild.createdAt, game.gameDayCount) : '-',
      'Статус': game.isActive ? 'Активна' : 'Неактивна',
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Игры');
    XLSX.writeFile(wb, 'partner-games.xlsx');
  };

  // Фильтрация игр по периоду
  const filteredGames = games.filter(game => {
    if (!dateFrom && !dateTo) return true;
    const created = new Date(game.createdAt);
    let from = dateFrom ? new Date(dateFrom) : null;
    let to = dateTo ? new Date(dateTo) : null;
    if (from && created < from) return false;
    if (to) {
      // включительно до конца дня
      to.setHours(23,59,59,999);
      if (created > to) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="partner-dashboard">
        <div className="partner-dashboard__loading">Загрузка...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="partner-dashboard">
        <div className="partner-dashboard__error">Ошибка загрузки профиля</div>
      </div>
    );
  }

  return (
    <div className="partner-dashboard">
      <header className="partner-dashboard__header">
        <div className="partner-dashboard__header-title">Партнерская панель</div>
        <div className="partner-dashboard__stats-row">
          <div className="partner-dashboard__stat-block-inline">
            <span className="partner-dashboard__stat-label-inline">Создано за месяц:</span>
            <span className="partner-dashboard__stat-value-inline">{monthlyStats}</span>
          </div>
          <div className="partner-dashboard__stat-block-inline">
            <span className="partner-dashboard__stat-label-inline">Доступно ключей:</span>
            <span className="partner-dashboard__stat-value-inline">{profile.keysAvailable}</span>
          </div>
        </div>
        <div className="partner-dashboard__header-user">
          <span>{profile.email}</span>
          <button onClick={handleLogout} className="partner-dashboard__logout">Выйти</button>
        </div>
      </header>

      <div className="partner-dashboard__content">
        {/* Форма создания игры */}
        <div className="partner-dashboard__create-section">
          <div className="partner-dashboard__section-title">Создать ключ</div>
          <form onSubmit={handleCreateGame} className="partner-dashboard__create-form">
            <div className="partner-dashboard__form-row">
              <div className="partner-dashboard__field">
                <label>Выберите игру:</label>
                <select
                  value={createForm.gameTitle}
                  onChange={(e) => setCreateForm({ ...createForm, gameTitle: e.target.value })}
                  required
                >
                  <option value="">Выберите игру</option>
                  {availableGames.map((game) => (
                    <option key={game} value={game}>{game}</option>
                  ))}
                </select>
              </div>
              
              <div className="partner-dashboard__field">
                <label>Заметка (необязательно):</label>
                <input
                  type="text"
                  value={createForm.notion}
                  onChange={(e) => setCreateForm({ ...createForm, notion: e.target.value })}
                  placeholder="Описание или заметка к игре"
                />
              </div>
              
              <div className="partner-dashboard__field">
                <label>URL кнопки (необязательно):</label>
                <input
                  type="url"
                  value={createForm.urlButton}
                  onChange={(e) => setCreateForm({ ...createForm, urlButton: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>
              
              <div className="partner-dashboard__field">
                <label>Количество ключей:</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={createForm.keyCount}
                  onChange={(e) => setCreateForm({ ...createForm, keyCount: parseInt(e.target.value) || 1 })}
                />
              </div>
              
              <div className="partner-dashboard__field">
                <button type="submit" className="partner-dashboard__create-btn">
                  Создать ключ
                </button>
              </div>
            </div>
          </form>
        </div>

        {error && (
          <div className="partner-dashboard__error">
            {error}
          </div>
        )}

        <div className="partner-dashboard__games-toolbar">
          <button className="partner-dashboard__export-btn" onClick={handleExportExcel}>
            Экспорт в Excel
          </button>
        </div>

        <div className="partner-dashboard__games">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12}}>
            <div className="partner-dashboard__section-title">Ключи ({filteredGames.length})</div>
            <div style={{display: 'flex', gap: 12, alignItems: 'center', fontSize: '1.15rem', background: 'none', paddingRight: 32}}>
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
              />
              <span style={{fontSize: '1.15rem'}}>—</span>
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
              />
            </div>
          </div>
          
          {filteredGames.length === 0 ? (
            <div className="partner-dashboard__empty">
              Нет игр за выбранный период.
            </div>
          ) : (
            <div className="partner-dashboard__table">
              <table>
                <thead>
                  <tr>
                    <th>Название игры</th>
                    <th>Email игрока</th>
                    <th>Заметка</th>
                    <th>Дата создания</th>
                    <th>Ключ игры</th>
                    <th>Ссылки</th>
                    <th>Запусков</th>
                    <th>Дата активации</th>
                    <th>Редактирование до</th>
                    <th>Дата отключения</th>
                    <th>Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGames.map((game) => (
                    <tr key={game._id} className={!game.isActive ? 'partner-dashboard__row--inactive' : ''}>
                      <td>{game.gameName}</td>
                      <td>{game.email || '-'}</td>
                      <td>{game.notion || '-'}</td>
                      <td>{formatDate(game.createdAt)}</td>
                      <td>
                        <code>{game.key}</code>
                        <button 
                          onClick={() => copyToClipboard(game.key)}
                          className="partner-dashboard__copy-btn"
                        >
                          Копировать
                        </button>
                      </td>
                      <td>
                        <div className="partner-dashboard__links">
                          <div className="partner-dashboard__link-group">
                            <button 
                              onClick={() => handleEditorClick(game.key)}
                              className="partner-dashboard__link-btn"
                            >
                              Редактор
                            </button>
                            <button 
                              onClick={() => copyToClipboard(getGameUrl(game.key))}
                              className="partner-dashboard__copy-icon-btn"
                              title="Копировать ссылку на редактор"
                            >
                              📋
                            </button>
                          </div>
                          {game.isCreated && game.gameBuild && (
                            <div className="partner-dashboard__link-group">
                              <button 
                                onClick={() => handleGameClick(game.gameBuildId)}
                                className="partner-dashboard__link-btn"
                              >
                                Игра
                              </button>
                              <button 
                                onClick={() => copyToClipboard(getPlayUrl(game.gameBuildId))}
                                className="partner-dashboard__copy-icon-btn"
                                title="Копировать ссылку на игру"
                              >
                                📋
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="partner-dashboard__runs">{game.runCount}</td>
                      <td>{game.activateDate ? formatDate(game.activateDate) : '-'}</td>
                      <td>{(game.gameBuild && game.gameBuild.createdAt) ? calculateEndDate(game.gameBuild.createdAt, game.editDayCount) : '-'}</td>
                      <td>{game.activateDate ? formatGameEndDate(new Date(game.activateDate), game.gameDayCount) : '-'}</td>
                      <td>
                        <span className={`partner-dashboard__status partner-dashboard__status--${game.isActive ? 'active' : 'inactive'}`}>
                          {game.isActive ? 'Активна' : 'Неактивна'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>


    </div>
  );
};

export default Dashboard; 